import React, { useState, useMemo } from "react";
import {
  Button,
  TextField,
  Box,
  Container,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Dna,
  Activity,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Import types and utilities
import type { ApiError } from "@/types/auth.type";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import { container } from "@/container/DependencyContainer";
import {
  formatPasswordComplexitySummary,
  getPasswordComplexitySummaryErrors,
  PASSWORD_REQUIREMENTS_HINT,
} from "@/utils/passwordValidation";

interface ResetPasswordPageProps {
  onBackToLogin?: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  onBackToLogin,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Password validation
  const passwordValidation = useMemo(() => {
    if (!password) {
      return { isValid: false, errors: [], status: "empty" };
    }

    const errors = getPasswordComplexitySummaryErrors(password);

    return {
      isValid: errors.length === 0,
      errors,
      status: errors.length === 0 ? "valid" : "invalid",
    };
  }, [password]);

  // Confirm password validation
  const confirmPasswordValidation = useMemo(() => {
    if (!confirmPassword) {
      return { isValid: false, message: "", status: "empty" };
    }

    if (confirmPassword !== password) {
      return {
        isValid: false,
        message: "Passwords do not match",
        status: "invalid",
      };
    }

    if (passwordValidation.isValid) {
      return {
        isValid: true,
        message: "Passwords match",
        status: "valid",
      };
    }

    return {
      isValid: false,
      message: "Password must meet all requirements first",
      status: "invalid",
    };
  }, [confirmPassword, password, passwordValidation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate token
      if (!token) {
        throw new Error(
          "Invalid reset link. Please request a new password reset.",
        );
      }

      if (!password) {
        throw new Error("Password is required");
      }

      if (!confirmPassword) {
        throw new Error("Please confirm your password");
      }

      if (!passwordValidation.isValid) {
        throw new Error(
          formatPasswordComplexitySummary(password) ||
            "Password does not meet security requirements",
        );
      }

      if (password !== confirmPassword) {
        throw new Error("Passwords do not match");
      }

      // Call the reset password use case
      await container.resetPasswordUseCase.execute({
        token,
        password,
      });

      setSuccessMessage("Password has been reset successfully!");
      setResetSuccess(true);
      setSnackbarOpen(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (apiError) {
      const errorMessage =
        (apiError as ApiError)?.message ||
        "Failed to reset password. Please try again.";
      setError(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      navigate("/login");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #EBF4FF 0%, #E0F2FE 50%, #EBF4FF 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: CLINICAL_FONT_FAMILY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.1,
          "& .dna-1": {
            position: "absolute",
            top: "5rem",
            left: "5rem",
            color: "#3B82F6",
            animation: "pulse 2s infinite",
          },
          "& .activity-1": {
            position: "absolute",
            top: "10rem",
            right: "8rem",
            color: "#14B8A6",
            animation: "pulse 2s infinite 1s",
          },
        }}
      >
        <Dna className="dna-1" size={64} />
        <Activity className="activity-1" size={80} />
      </Box>

      {/* Main Container */}
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 10,
          py: 4,
        }}
      >
        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          <Box
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Header */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 600,
                  color: "#1F2937",
                  mb: 0.5,
                }}
              >
                Set New Password
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#6B7280",
                }}
              >
                Enter your new password to regain access to your account
              </Typography>
            </Box>

            {!resetSuccess ? (
              <>
                {/* Error Alert - Invalid Token */}
                {!token && (
                  <Alert
                    severity="error"
                    icon={<AlertCircle size={20} />}
                    sx={{
                      backgroundColor: "#FEE2E2",
                      color: "#991B1B",
                      border: "1px solid #FECACA",
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontSize: "0.9rem",
                      },
                    }}
                  >
                    Invalid reset link. Please request a new password reset.
                  </Alert>
                )}

                {/* Password Requirements Info */}
                <Alert
                  severity="info"
                  icon={<Lock size={20} />}
                  sx={{
                    backgroundColor: "#EFF6FF",
                    color: "#1E40AF",
                    border: "1px solid #BFDBFE",
                    borderRadius: 2,
                    "& .MuiAlert-message": {
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  {PASSWORD_REQUIREMENTS_HINT}
                </Alert>

                {/* Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit(e);
                  }}
                >
                  {/* New Password Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                      }}
                    >
                      New Password <span style={{ color: "#DC2626" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading || !token}
                      error={
                        passwordValidation.status === "invalid" &&
                        password.length > 0
                      }
                      InputProps={{
                        startAdornment: (
                          <Lock
                            size={20}
                            style={{
                              marginRight: 12,
                              color:
                                passwordValidation.status === "valid"
                                  ? "#10B981"
                                  : passwordValidation.status === "invalid" &&
                                      password.length > 0
                                    ? "#DC2626"
                                    : "#9CA3AF",
                            }}
                          />
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                void setShowPassword(!showPassword);
                              }}
                              edge="end"
                              sx={{
                                color: "#9CA3AF",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#6B7280",
                                },
                              }}
                            >
                              {showPassword ? (
                                <EyeOff size={20} />
                              ) : (
                                <Eye size={20} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontFamily: CLINICAL_FONT_FAMILY,
                          backgroundColor: "#F9FAFB",
                          border: "1.5px solid",
                          borderColor:
                            passwordValidation.status === "valid"
                              ? "#10B981"
                              : passwordValidation.status === "invalid" &&
                                  password.length > 0
                                ? "#DC2626"
                                : "#E5E7EB",
                          borderRadius: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor:
                              passwordValidation.status === "valid"
                                ? "#059669"
                                : passwordValidation.status === "invalid" &&
                                    password.length > 0
                                  ? "#B91C1C"
                                  : "#D1D5DB",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor:
                              passwordValidation.status === "valid"
                                ? "#10B981"
                                : passwordValidation.status === "invalid" &&
                                    password.length > 0
                                  ? "#DC2626"
                                  : "#3B82F6",
                            boxShadow:
                              passwordValidation.status === "valid"
                                ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                                : passwordValidation.status === "invalid" &&
                                    password.length > 0
                                  ? "0 0 0 3px rgba(220, 38, 38, 0.1)"
                                  : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input::placeholder": {
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                    />
                    {/* Password Requirements List */}
                    {password && passwordValidation.errors.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        {passwordValidation.errors.map((error, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              fontFamily: CLINICAL_FONT_FAMILY,
                              color: "#DC2626",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AlertCircle size={14} />
                            Missing: {error}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {passwordValidation.isValid && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.75,
                          fontFamily: CLINICAL_FONT_FAMILY,
                          color: "#10B981",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CheckCircle size={14} />
                        Password meets all requirements
                      </Typography>
                    )}
                  </Box>

                  {/* Confirm Password Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                      }}
                    >
                      Confirm Password{" "}
                      <span style={{ color: "#DC2626" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={
                        loading || !token || !passwordValidation.isValid
                      }
                      error={
                        confirmPasswordValidation.status === "invalid" &&
                        confirmPassword.length > 0
                      }
                      InputProps={{
                        startAdornment: (
                          <Lock
                            size={20}
                            style={{
                              marginRight: 12,
                              color:
                                confirmPasswordValidation.status === "valid"
                                  ? "#10B981"
                                  : confirmPasswordValidation.status ===
                                        "invalid" && confirmPassword.length > 0
                                    ? "#DC2626"
                                    : "#9CA3AF",
                            }}
                          />
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              sx={{
                                color: "#9CA3AF",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#6B7280",
                                },
                              }}
                            >
                              {showConfirmPassword ? (
                                <EyeOff size={20} />
                              ) : (
                                <Eye size={20} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontFamily: CLINICAL_FONT_FAMILY,
                          backgroundColor: "#F9FAFB",
                          border: "1.5px solid",
                          borderColor:
                            confirmPasswordValidation.status === "valid"
                              ? "#10B981"
                              : confirmPasswordValidation.status ===
                                    "invalid" && confirmPassword.length > 0
                                ? "#DC2626"
                                : "#E5E7EB",
                          borderRadius: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor:
                              confirmPasswordValidation.status === "valid"
                                ? "#059669"
                                : confirmPasswordValidation.status ===
                                      "invalid" && confirmPassword.length > 0
                                  ? "#B91C1C"
                                  : "#D1D5DB",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor:
                              confirmPasswordValidation.status === "valid"
                                ? "#10B981"
                                : confirmPasswordValidation.status ===
                                      "invalid" && confirmPassword.length > 0
                                  ? "#DC2626"
                                  : "#3B82F6",
                            boxShadow:
                              confirmPasswordValidation.status === "valid"
                                ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                                : confirmPasswordValidation.status ===
                                      "invalid" && confirmPassword.length > 0
                                  ? "0 0 0 3px rgba(220, 38, 38, 0.1)"
                                  : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input::placeholder": {
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                    />
                    {/* Confirm Password Message */}
                    {confirmPassword && confirmPasswordValidation.message && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.75,
                          fontFamily: CLINICAL_FONT_FAMILY,
                          color:
                            confirmPasswordValidation.status === "valid"
                              ? "#10B981"
                              : "#DC2626",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {confirmPasswordValidation.status === "valid" ? (
                          <CheckCircle size={14} />
                        ) : (
                          <AlertCircle size={14} />
                        )}
                        {confirmPasswordValidation.message}
                      </Typography>
                    )}
                  </Box>

                  {/* Reset Password Button */}
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={
                      loading || !token || !confirmPasswordValidation.isValid
                    }
                    sx={{
                      py: 1.75,
                      mb: 2,
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontWeight: 600,
                      fontSize: "1rem",
                      textTransform: "none",
                      background:
                        "linear-gradient(90deg, #0EA5E9 0%, #06B6D4 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover:not(:disabled)": {
                        boxShadow: "0 10px 25px -5px rgba(6, 182, 212, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {loading && <CircularProgress size={20} color="inherit" />}
                    Reset Password
                  </Button>
                </form>
              </>
            ) : (
              /* Success State */
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    margin: "0 auto",
                    mb: 2,
                    borderRadius: "50%",
                    backgroundColor: "#ECFDF5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle size={40} style={{ color: "#10B981" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 1,
                  }}
                >
                  Password Reset Successfully
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: CLINICAL_FONT_FAMILY,
                    color: "#6B7280",
                    mb: 3,
                  }}
                >
                  Your password has been reset. Redirecting to login...
                </Typography>
              </Box>
            )}

            {/* Back to Sign In Link */}
            {!resetSuccess && (
              <Button
                onClick={handleBackToLogin}
                startIcon={<ArrowLeft size={20} />}
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#0EA5E9",
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Back to Sign in
              </Button>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Snackbar for errors and success */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={error ? "error" : "success"}
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            width: "100%",
          }}
        >
          {error ?? successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPasswordPage;
